import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Stack,
} from "@material-ui/core";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "src/redux/store";
import SupportPicker from "src/components/SupportPicker";
import { getAddOns } from "src/redux/slices/addOns";
import { readPendingSelection, savePendingSelection } from "src/utils/pendingSelection";

// Wizard · Step 9 · Soporte (radio exclusivo).
// Estándar 29€/mes o Prioritario 69€/mes — o Básico gratis (incluido
// en cualquier plan). Se persiste en `pendingSelection.addOnIds`
// junto con los extras, manteniendo consistencia.

export default function StepSupport({ onComplete, onSkip }) {
  const dispatch = useDispatch();
  const { addOns } = useSelector((s) => s.addOns);
  const [supportKind, setSupportKind] = useState(null);
  const [initialised, setInitialised] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!addOns || addOns.length === 0) dispatch(getAddOns(true));
  }, [dispatch, addOns]);

  const pending = useMemo(() => readPendingSelection(), []);

  // Hidratación: si hay un add-on de soporte en el pendingSelection,
  // lo pre-marcamos como valor del radio.
  useEffect(() => {
    if (initialised) return;
    if (!addOns || addOns.length === 0) return;
    if (pending?.addOnIds?.length) {
      const supportId = pending.addOnIds.find((id) => {
        const a = addOns.find((x) => x.id === id);
        return a?.kind?.startsWith("support");
      });
      if (supportId) {
        const s = addOns.find((x) => x.id === supportId);
        if (s) setSupportKind(s.kind);
      }
    }
    setInitialised(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOns]);

  const handleContinue = () => {
    setSaving(true);
    try {
      const existing = readPendingSelection() || {
        addOnIds: [],
        savedAt: new Date().toISOString(),
      };
      // Preservar extras (no-soporte) y sustituir el soporte anterior
      // por el nuevo (o quitarlo si el user eligió Básico gratis).
      const nonSupportIds = (existing.addOnIds || []).filter((id) => {
        const a = addOns?.find((x) => x.id === id);
        return a && !a.kind?.startsWith("support");
      });
      const supportAddOn = supportKind
        ? addOns?.find((a) => a.kind === supportKind)
        : null;
      const newIds = [
        ...nonSupportIds,
        ...(supportAddOn ? [supportAddOn.id] : []),
      ];
      savePendingSelection({
        ...existing,
        addOnIds: newIds,
        savedAt: new Date().toISOString(),
      });
      onComplete?.({
        dataJson: JSON.stringify({ supportKind }),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Nivel de soporte
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }}>
        Un TPV caído un sábado noche es una noche perdida. Elige el nivel de
        respuesta que necesitas — Básico va incluido siempre.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        El soporte Prioritario tiene <strong>cupo limitado</strong> — para
        garantizar el SLA no vendemos plazas ilimitadas.
      </Alert>

      <SupportPicker value={supportKind} onChange={setSupportKind} />

      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
        <Button color="inherit" onClick={onSkip} disabled={saving}>
          Dejar Básico
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          onClick={handleContinue}
          sx={{ minWidth: 180 }}
        >
          Guardar soporte
        </LoadingButton>
      </Stack>
    </Box>
  );
}
